import React, { useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import { graphThemes } from "../../themes/graphThemes";

const PURPLE_LIGHT = "#7c3aed";
const MATH_FONT = "KaTeX_Main, Cambria Math, STIX Two Math, Georgia, 'Times New Roman', serif";

function segmentPoint(p, q, t) {
  return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
}

function unit(p, q) {
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

function extendedLine(p, q, before = 28, after = 28) {
  const u = unit(p, q);
  return {
    x1: p.x - u.x * before,
    y1: p.y - u.y * before,
    x2: q.x + u.x * after,
    y2: q.y + u.y * after,
  };
}

function Label({ x, y, children, fill = "currentColor", italic = false, size = 22, anchor = "middle" }) {
  return (
    <text
      x={x}
      y={y}
      fill={fill}
      fontFamily={MATH_FONT}
      fontSize={size}
      fontStyle={italic ? "italic" : "normal"}
      textAnchor={anchor}
      dominantBaseline="middle"
    >
      {children}
    </text>
  );
}

function AngleArc({ vertex, from, to, color, radius = 18 }) {
  const startAngle = Math.atan2(from.y - vertex.y, from.x - vertex.x);
  let delta = Math.atan2(to.y - vertex.y, to.x - vertex.x) - startAngle;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  const endAngle = startAngle + delta;
  const start = { x: vertex.x + radius * Math.cos(startAngle), y: vertex.y + radius * Math.sin(startAngle) };
  const end = { x: vertex.x + radius * Math.cos(endAngle), y: vertex.y + radius * Math.sin(endAngle) };

  return (
    <path
      d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 0 ${delta > 0 ? 1 : 0} ${end.x} ${end.y}`}
      fill="none"
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
    />
  );
}

// Miękka klamra (jak \overbrace w KaTeX) nad całym odcinkiem P→Q, z zawijasem
// w środku, przy którym siada podpis długości całości.
function Brace({ p, q, label, up = true, height = 34, color = "currentColor" }) {
  const u = unit(p, q);
  let n = { x: -u.y, y: u.x };
  if ((up && n.y > 0) || (!up && n.y < 0)) n = { x: -n.x, y: -n.y };
  // P(t, h): t wzdłuż odcinka (0..1), h na zewnątrz wzdłuż normalnej.
  const P = (t, h) => ({
    x: p.x + (q.x - p.x) * t + n.x * h,
    y: p.y + (q.y - p.y) * t + n.y * h,
  });
  const H = height;
  const cf = 0.13; // długość zaokrąglonego narożnika (ułamek odcinka)
  const dn = 0.04; // połowa szerokości dziubka w środku
  const spike = 7; // wysokość dziubka
  const A = P(0, 0);
  const cL = P(0, H);
  const L1 = P(cf, H);
  const Lm = P(0.5 - dn, H);
  const tip = P(0.5, H + spike);
  const Rm = P(0.5 + dn, H);
  const R1 = P(1 - cf, H);
  const cR = P(1, H);
  const Bn = P(1, 0);
  // krótki zaokrąglony narożnik → pozioma linia → dziubek → analogicznie z powrotem
  const path =
    `M ${A.x} ${A.y} Q ${cL.x} ${cL.y} ${L1.x} ${L1.y} ` +
    `L ${Lm.x} ${Lm.y} L ${tip.x} ${tip.y} L ${Rm.x} ${Rm.y} ` +
    `L ${R1.x} ${R1.y} Q ${cR.x} ${cR.y} ${Bn.x} ${Bn.y}`;
  const lp = P(0.5, H + spike + 14);
  return (
    <g>
      <path d={path} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
      <Label x={lp.x} y={lp.y} fill={color} size={20}>{label}</Label>
    </g>
  );
}

export function ThalesCrossedDiagram({ labels = {}, showSimilarAngles = false }) {
  const { theme } = useContext(ThemeContext) || {};
  const graphStyle = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];
  const colors = {
    text: "var(--color-text, #f8fafc)",
    accent: "var(--color-accent, #f59e0b)",
    measure: theme === "dark" ? graphStyle.lineColor : PURPLE_LIGHT,
    point: "var(--color-text, #f8fafc)",
  };

  const leftX = 135;
  const rightX = 365;
  const ratio = Number(labels.OA) > 0 && Number(labels.OC) > 0
    ? Number(labels.OA) / Number(labels.OC)
    : 1;
  const ox = (ratio * rightX + leftX) / (ratio + 1);
  const O = { x: ox, y: 150 };
  const slopeAC = -0.56;
  const slopeDB = 0.5;
  const points = {
    A: { x: leftX, y: O.y + slopeAC * (leftX - O.x) },
    B: { x: rightX, y: O.y + slopeDB * (rightX - O.x) },
    C: { x: rightX, y: O.y + slopeAC * (rightX - O.x) },
    D: { x: leftX, y: O.y + slopeDB * (leftX - O.x) },
    O,
  };
  const lineAC = extendedLine(points.A, points.C, 66, 66);
  const lineDB = extendedLine(points.D, points.B, 66, 66);

  const measurePosition = (p, q, side) => {
    const mid = segmentPoint(p, q, 0.5);
    const u = unit(p, q);
    const normal = side > 0
      ? { x: -u.y, y: u.x }
      : { x: u.y, y: -u.x };
    return { x: mid.x + normal.x * 17, y: mid.y + normal.y * 17 };
  };

  const measurePositions = {
    OA: measurePosition(points.A, O, 1),
    OC: measurePosition(O, points.C, -1),
    OD: measurePosition(points.D, O, -1),
    OB: measurePosition(O, points.B, 1),
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
      <svg width="520" height="310" viewBox="0 0 520 310" role="img" aria-label="Dwie proste równoległe przecięte dwiema siecznymi w punkcie O">
        <line x1={leftX} y1="35" x2={leftX} y2="275" stroke={colors.accent} strokeWidth="4" strokeLinecap="round" />
        <line x1={rightX} y1="35" x2={rightX} y2="275" stroke={colors.accent} strokeWidth="4" strokeLinecap="round" />
        <line {...lineAC} stroke={colors.text} strokeWidth="3" strokeLinecap="round" />
        <line {...lineDB} stroke={colors.text} strokeWidth="3" strokeLinecap="round" />

        {showSimilarAngles && (
          <>
            <AngleArc vertex={points.A} from={O} to={points.D} color="#a855f7" />
            <AngleArc vertex={points.C} from={O} to={points.B} color="#a855f7" />
            <AngleArc vertex={points.D} from={O} to={points.A} color="#ff9800" />
            <AngleArc vertex={points.B} from={O} to={points.C} color="#ff9800" />
            <AngleArc vertex={O} from={points.A} to={points.D} color="#22d3ee" radius={13} />
            <AngleArc vertex={O} from={points.C} to={points.B} color="#22d3ee" radius={13} />
          </>
        )}

        {Object.entries(points).map(([name, point]) => (
          <circle key={name} cx={point.x} cy={point.y} r="5" fill={colors.point} />
        ))}

        <Label x={points.A.x - 18} y={points.A.y - 18} fill={colors.text} italic>A</Label>
        <Label x={points.B.x + 18} y={points.B.y - 18} fill={colors.text} italic>B</Label>
        <Label x={points.C.x + 18} y={points.C.y + 18} fill={colors.text} italic>C</Label>
        <Label x={points.D.x - 18} y={points.D.y + 18} fill={colors.text} italic>D</Label>
        <Label x={O.x} y={O.y + 25} fill={colors.text} italic>O</Label>
        <Label x={leftX - 18} y="27" fill={colors.text} italic size={20}>k</Label>
        <Label x={rightX - 18} y="27" fill={colors.text} italic size={20}>l</Label>
        <Label x={lineAC.x1 - 10} y={lineAC.y1 + 16} fill={colors.text} italic size={20}>m</Label>
        <Label x={lineDB.x1 - 10} y={lineDB.y1 - 14} fill={colors.text} italic size={20}>n</Label>

        {labels.OA ? <Label {...measurePositions.OA} fill={colors.measure} size={20}>{labels.OA}</Label> : null}
        {labels.OC ? <Label {...measurePositions.OC} fill={colors.measure} size={20}>{labels.OC}</Label> : null}
        {labels.OB ? <Label {...measurePositions.OB} fill={colors.measure} size={20}>{labels.OB}</Label> : null}
        {labels.OD ? <Label {...measurePositions.OD} fill={colors.measure} size={20}>{labels.OD}</Label> : null}
      </svg>
    </div>
  );
}

export default function ThalesTriangleDiagram({
  labels = {},
  nearRatio = 0.524,
  showParallelNote = true,
  upperBrace = null,
  lowerBrace = null,
  chordLabels = {},
  showSimilarAngles = false,
}) {
  const { theme } = useContext(ThemeContext) || {};
  const graphStyle = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];
  const colors = {
    text: "var(--color-text, #f8fafc)",
    accent: "var(--color-accent, #f59e0b)",
    measure: theme === "dark" ? graphStyle.lineColor : PURPLE_LIGHT,
    point: "var(--color-text, #f8fafc)",
  };

  const O = { x: 80, y: 78 };
  const upperEnd = { x: 438, y: 78 };
  const lowerEnd = { x: 418, y: 232 };
  const tFar = 0.82;
  // B i A leżą na tym samym parametrze t obu promieni, więc cięciwy AB i CD są
  // równoległe niezależnie od danych. t skalujemy proporcjonalnie do OB/OD (= OA/OC),
  // dzięki czemu rysunek wiernie oddaje proporcje konkretnego zadania.
  const ratio = Math.min(0.85, Math.max(0.14, nearRatio));
  const tNear = tFar * ratio;

  const B = segmentPoint(O, upperEnd, tNear);
  const D = segmentPoint(O, upperEnd, tFar);
  const A = segmentPoint(O, lowerEnd, tNear);
  const C = segmentPoint(O, lowerEnd, tFar);
  const AB = extendedLine(A, B, 32, 28);
  const CD = extendedLine(C, D, 34, 30);

  const topNear = segmentPoint(O, B, 0.52);
  const topFar = segmentPoint(B, D, 0.52);
  const bottomNear = segmentPoint(O, A, 0.55);
  const bottomFar = segmentPoint(A, C, 0.52);

  // Etykiety odcinków zostają po swojej stronie linii. Gdy jest klamra,
  // wciskamy je blisko linii (klamra idzie dalej, ponad oznaczenia punktów).
  const topY = (p) => p.y + (upperBrace ? -14 : -22);
  const bottomY = (p) => p.y + (lowerBrace ? 16 : 26);

  const note = segmentPoint(segmentPoint(A, B, 0.5), segmentPoint(C, D, 0.5), 0.5);

  // Pozycja etykiety cięciwy: od środka cięciwy w kierunku „od O" (away) lub „do O".
  const placeChord = (p, q, away) => {
    const mid = segmentPoint(p, q, 0.5);
    const uu = unit(p, q);
    let nn = { x: -uu.y, y: uu.x };
    const toMid = { x: mid.x - O.x, y: mid.y - O.y };
    if (nn.x * toMid.x + nn.y * toMid.y < 0) nn = { x: -nn.x, y: -nn.y };
    const s = away ? 1 : -1;
    return { x: mid.x + nn.x * 19 * s, y: mid.y + nn.y * 19 * s };
  };
  const abPos = placeChord(A, B, false);
  const cdPos = placeChord(C, D, true);

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
      <svg width="520" height="278" viewBox="0 0 520 278" role="img" aria-label="Rysunek do twierdzenia Talesa">
        <line x1={O.x} y1={O.y} x2={upperEnd.x} y2={upperEnd.y} stroke={colors.text} strokeWidth="3" strokeLinecap="round" />
        <line x1={O.x} y1={O.y} x2={lowerEnd.x} y2={lowerEnd.y} stroke={colors.text} strokeWidth="3" strokeLinecap="round" />

        <line {...AB} stroke={colors.accent} strokeWidth="4" strokeLinecap="round" />
        <line {...CD} stroke={colors.accent} strokeWidth="4" strokeLinecap="round" />

        {showSimilarAngles ? (
          <>
            <AngleArc vertex={A} from={O} to={B} color="#f59e0b" />
            <AngleArc vertex={C} from={O} to={D} color="#f59e0b" />
            <AngleArc vertex={B} from={O} to={A} color="#06b6d4" />
            <AngleArc vertex={D} from={O} to={C} color="#06b6d4" />
            <AngleArc vertex={O} from={B} to={A} color="#a855f7" radius={16} />
          </>
        ) : null}

        {[O, A, B, C, D].map((p, index) => (
          <circle key={index} cx={p.x} cy={p.y} r="5" fill={colors.point} />
        ))}

        <Label x={O.x - 22} y={O.y - 22} fill={colors.text} italic>O</Label>
        <Label x={A.x - 24} y={A.y + 22} fill={colors.text} italic>A</Label>
        <Label x={B.x + 22} y={B.y - 24} fill={colors.text} italic>B</Label>
        <Label x={C.x - 22} y={C.y + 20} fill={colors.text} italic>C</Label>
        <Label x={D.x + 24} y={D.y - 24} fill={colors.text} italic>D</Label>

        {labels.topNear ? <Label x={topNear.x} y={topY(topNear)} fill={colors.measure} size={20}>{labels.topNear}</Label> : null}
        {labels.topFar ? <Label x={topFar.x} y={topY(topFar)} fill={colors.measure} size={20}>{labels.topFar}</Label> : null}
        {labels.bottomNear ? <Label x={bottomNear.x - 8} y={bottomY(bottomNear)} fill={colors.measure} size={20}>{labels.bottomNear}</Label> : null}
        {labels.bottomFar ? <Label x={bottomFar.x - 4} y={bottomY(bottomFar)} fill={colors.measure} size={20}>{labels.bottomFar}</Label> : null}

        {upperBrace ? <Brace p={O} q={D} label={upperBrace} up height={40} color={colors.measure} /> : null}
        {lowerBrace ? <Brace p={O} q={C} label={lowerBrace} up={false} height={40} color={colors.measure} /> : null}

        {chordLabels.ab ? <Label x={abPos.x} y={abPos.y} fill={colors.measure} size={18}>{chordLabels.ab}</Label> : null}
        {chordLabels.cd ? <Label x={cdPos.x} y={cdPos.y} fill={colors.measure} size={18}>{chordLabels.cd}</Label> : null}

        {showParallelNote ? (
          <Label x={note.x} y={note.y} fill={colors.accent} size={18}>AB ∥ CD</Label>
        ) : null}
      </svg>
    </div>
  );
}
